<script setup lang="ts">
import InstallerProfile from "@/components/installer/InstallerProfile.vue";
import InlineLoader from "@/components/Loaders/InlineLoader.vue";
import SecondaryPageHeader from "@/components/PageHeader/SecondaryPageHeader.vue";
import InstallersSidemenu from "@/components/Sidebar/installer/InstallersSidemenu.vue";
import MainLayout from "@/layouts/MainLayout.vue";
import { useUserStore } from "@/stores/user";
import { InstallerDetails, Worker } from "@/types";
import client from "@/utils/axios";
import { computed, onMounted, ref, watch } from "vue";
import { useRoute } from "vue-router";

// Route
const route = useRoute();
const installerDetails = ref<InstallerDetails | null>(null);

// Store
const userStore = useUserStore();

// Refs
const workers = ref<Worker[]>([]);
const selectedWorker = ref<string | null>(null);
const selectedMonths = ref<Record<string, { month: number; year: number }>>({});
const isLoading = ref<boolean>(false);

// Functions
const getAllInstallers = async () => {
  isLoading.value = true;
  try {
    const response = await client.get("/installers");
    workers.value = response.data;
  } catch (error) {
    console.log(error);
  } finally {
    isLoading.value = false;
  }
};

const getInstallerDetails = async () => {
  try {
    isLoading.value = true;
    const response = await client.get(
      `/installers/${route.params.id}?locale=${currentLanguage.value}`
    );
    isLoading.value = false;
    installerDetails.value = response.data;
  } catch (error) {
    console.log(error);
    isLoading.value = false;
  }
};

const handleSelectWorker = (workerId: string) => {
  if (selectedWorker.value === workerId) {
    selectedWorker.value = null;
  } else {
    selectedWorker.value = workerId;

    if (!selectedMonths.value[workerId]) {
      selectedMonths.value[workerId] = {
        month: new Date().getMonth(),
        year: new Date().getFullYear(),
      };
    }
  }
};

const handleSelectMonth = (workerId: string, month: number, year: number) => {
  selectedMonths.value[workerId] = { month, year };
};

// Computed properties
const selectedWorkerProfile = computed(() => {
  return workers.value.find((item: Worker) => item.id === route.params.id);
});

const currentLanguage = computed(() => userStore.language);
let lastDatabaseTranslationUpdateTimestamp = computed(
  () => userStore.translationsUpdateTimestamp
);

// Watchers
watch(selectedWorkerProfile, (newValue?: Worker) => {
  if (newValue) {
    handleSelectWorker(newValue.id);
  }
});

watch(
  currentLanguage,
  async (newLang: string, oldLang?: string) => {
    if (newLang && oldLang && newLang !== oldLang) {
      await getInstallerDetails();
    }
  },
  { immediate: true }
);

watch(
  lastDatabaseTranslationUpdateTimestamp,
  async (newTimestamp: number, oldTimestamp?: number) => {
    if (newTimestamp && oldTimestamp && newTimestamp !== oldTimestamp) {
      await getInstallerDetails();
    }
  },
  { immediate: true }
);

watch(
  () => route.params.id as string,
  (newId: string, oldId: string) => {
    if (newId !== oldId) {
      getInstallerDetails();
      handleSelectWorker(newId);
    }
  }
);

// Lifecycle hooks
onMounted(() => {
  getAllInstallers();
  getInstallerDetails();
});
</script>

<template>
  <MainLayout>
    <SecondaryPageHeader
      :is-installer-profile="true"
      :selected-worker-profile="selectedWorkerProfile"
    />
    <div class="w-full h-[calc(100vh-52px)] flex">
      <div
        class="w-[190px] mt-10 flex flex-col items-center gap-3"
        v-if="isLoading"
      >
        <InlineLoader />
        <InlineLoader />
        <InlineLoader />
        <InlineLoader />
      </div>
      <div class="min-w-[190px] w-[190px]" v-else>
        <InstallersSidemenu
          v-if="installerDetails"
          :workers="workers"
          :selected-months="selectedMonths"
          :installer-details="installerDetails"
          @select-worker="handleSelectWorker"
          @select-report="handleSelectMonth"
        />
      </div>
      <div class="w-full">
        <InstallerProfile
          v-if="!isLoading"
          :worker-name="installerDetails?.worker?.fullname"
          :worker-role="installerDetails?.worker?.role"
          :completed-termins="installerDetails?.completedTermins"
          :completed-termins-percentage="
            installerDetails?.completedTerminsPercentage
          "
          :termins="installerDetails?.installationTermins"
          :worker-image="installerDetails?.worker?.image"
          :is-report="false"
        />
      </div>
    </div>
  </MainLayout>
</template>
